import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as firestoreService from '../services/firestoreService';
import * as storageService from '../services/storageService';
import { useAuth } from './AuthContext';

export interface ServiceDog {
  id: string;
  registryNumber: string;
  dogName: string;
  dogBreed: string;
  dogAge: string;
  dogPhoto: string;
  ownerName: string;
  ownerAddress: string;
  ownerPhone: string;
  ownerEmail: string;
  serviceType: string;
  certificationFile?: string;
  registrationDate: string;
  lastUpdated: string;
  status: 'active' | 'pending' | 'revoked';
}

interface RegistryContextType {
  serviceDogs: ServiceDog[];
  pendingDogs: ServiceDog[];
  loading: boolean;
  addServiceDog: (dog: Omit<ServiceDog, 'id' | 'registryNumber' | 'registrationDate' | 'lastUpdated' | 'status'>) => Promise<ServiceDog>;
  getServiceDogById: (id: string) => Promise<ServiceDog | null>;
  getServiceDogByRegistryNumber: (registryNumber: string) => Promise<ServiceDog | null>;
  updateServiceDog: (dog: ServiceDog) => Promise<ServiceDog>;
  deleteServiceDog: (id: string, status: 'active' | 'pending' | 'revoked') => Promise<boolean>;
  approveDog: (id: string) => Promise<ServiceDog>;
  denyDog: (id: string) => Promise<void>;
  refreshDogs: () => Promise<boolean>;
}

const RegistryContext = createContext<RegistryContextType | undefined>(undefined);

export const useRegistry = () => {
  const context = useContext(RegistryContext);
  if (!context) {
    throw new Error('useRegistry must be used within a RegistryProvider');
  }
  return context;
};

export const RegistryProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  const [serviceDogs, setServiceDogs] = useState<ServiceDog[]>([]);
  const [pendingDogs, setPendingDogs] = useState<ServiceDog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // We're not loading dogs automatically on mount to prevent auto-refresh issues
  // Instead, we'll rely on the refreshDogs function to be called manually
  useEffect(() => {
    // Just set loading to false initially
    setLoading(false);

    // Cleanup function
    return () => {
      // Nothing to clean up
    };
  }, []);

  // Refresh dogs from Firestore - this is called manually from the UI
  const refreshDogs = async () => {
    setLoading(true);

    try {
      // Load active dogs (public access) - these should always be accessible
      try {
        console.log('Loading active dogs...');
        const activeDogs = await firestoreService.getAllDogs();
        console.log(`Loaded ${activeDogs.length} active dogs`);
        setServiceDogs(activeDogs);
      } catch (error) {
        console.error('Error refreshing active dogs:', error);
        // Don't throw, just continue with empty array
        setServiceDogs([]);
      }

      // Only try to load pending dogs if user is authenticated
      if (isAuthenticated && user) {
        try {
          console.log('Loading pending dogs...');
          const pendingDogsData = await firestoreService.getPendingDogs();
          console.log(`Loaded ${pendingDogsData.length} pending dogs`);
          setPendingDogs(pendingDogsData);
        } catch (error) {
          // Log this error but don't show to user
          console.error('Error refreshing pending dogs:', error);
          setPendingDogs([]);
        }
      } else {
        // Not authenticated, so no pending dogs
        setPendingDogs([]);
      }

      return true; // Indicate success
    } catch (error) {
      console.error('Error in refreshDogs:', error);
      // Don't throw the error to prevent cascading failures
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Add a new dog registration (pending)
  const addServiceDog = async (dog: Omit<ServiceDog, 'id' | 'registryNumber' | 'registrationDate' | 'lastUpdated' | 'status'>) => {
    try {
      // We no longer need to sign in anonymously - the Firestore rules will allow public write access to pendingDogs
      console.log("Processing dog registration submission");

      // Process dog photo if it's a base64 string
      let photoUrl = dog.dogPhoto;
      if (dog.dogPhoto && dog.dogPhoto.startsWith('data:')) {
        try {
          // Generate a temporary ID for the file path
          const tempId = Math.random().toString(36).substring(2, 15);
          const photoPath = storageService.generateDogPhotoPath(tempId, dog.dogPhoto);
          photoUrl = await storageService.uploadImage(dog.dogPhoto, photoPath);
        } catch (uploadError) {
          console.error('Error uploading dog photo:', uploadError);
          // If we can't upload the photo, just use a placeholder URL
          photoUrl = "https://placehold.co/400x400?text=Dog+Photo";
        }
      }

      // Process certification file if it exists and is a base64 string
      let certUrl = dog.certificationFile;
      if (dog.certificationFile && dog.certificationFile.startsWith('data:')) {
        try {
          const tempId = Math.random().toString(36).substring(2, 15);
          const certPath = storageService.generateCertificationPath(tempId, dog.certificationFile);
          certUrl = await storageService.uploadDocument(dog.certificationFile, certPath);
        } catch (uploadError) {
          console.error('Error uploading certification file:', uploadError);
          // If we can't upload the certification, just use an empty string
          certUrl = "";
        }
      }

      // Create the dog with updated URLs
      const newDog = await firestoreService.addPendingDog({
        ...dog,
        dogPhoto: photoUrl,
        certificationFile: certUrl
      });

      // Update local state
      setPendingDogs(prev => [...prev, newDog]);

      return newDog;
    } catch (error) {
      console.error('Error adding service dog:', error);
      throw error;
    }
  };

  // Get a dog by ID
  const getServiceDogById = async (id: string) => {
    try {
      return await firestoreService.getDogById(id);
    } catch (error) {
      console.error('Error getting dog by ID:', error);
      throw error;
    }
  };

  // Get a dog by registry number
  const getServiceDogByRegistryNumber = async (registryNumber: string) => {
    try {
      return await firestoreService.getDogByRegistryNumber(registryNumber);
    } catch (error) {
      console.error('Error getting dog by registry number:', error);
      throw error;
    }
  };

  // Update a dog
  const updateServiceDog = async (dog: ServiceDog) => {
    try {
      // Get the original dog to check if status has changed
      const originalDog = await getServiceDogById(dog.id);
      if (!originalDog) {
        throw new Error(`Dog with ID ${dog.id} not found`);
      }

      const statusChanged = originalDog.status !== dog.status;

      // Process dog photo if it's a base64 string
      let photoUrl = dog.dogPhoto;
      if (dog.dogPhoto && dog.dogPhoto.startsWith('data:')) {
        const photoPath = storageService.generateDogPhotoPath(dog.id, dog.dogPhoto);
        photoUrl = await storageService.uploadImage(dog.dogPhoto, photoPath);
      }

      // Process certification file if it exists and is a base64 string
      let certUrl = dog.certificationFile;
      if (dog.certificationFile && dog.certificationFile.startsWith('data:')) {
        const certPath = storageService.generateCertificationPath(dog.id, dog.certificationFile);
        certUrl = await storageService.uploadDocument(dog.certificationFile, certPath);
      }

      // Update the dog with new URLs
      const updatedDog = await firestoreService.updateDog({
        ...dog,
        dogPhoto: photoUrl,
        certificationFile: certUrl
      });

      // Update local state based on status change
      if (statusChanged) {
        console.log(`Status changed from ${originalDog.status} to ${updatedDog.status}`);

        // Remove from original collection
        if (originalDog.status === 'active' || originalDog.status === 'revoked') {
          setServiceDogs(prev => prev.filter(d => d.id !== updatedDog.id));
        } else if (originalDog.status === 'pending') {
          setPendingDogs(prev => prev.filter(d => d.id !== updatedDog.id));
        }

        // Add to new collection
        if (updatedDog.status === 'active' || updatedDog.status === 'revoked') {
          setServiceDogs(prev => [...prev, updatedDog]);
        } else if (updatedDog.status === 'pending') {
          setPendingDogs(prev => [...prev, updatedDog]);
        }
      } else {
        // Status didn't change, just update in the same collection
        if (updatedDog.status === 'active' || updatedDog.status === 'revoked') {
          setServiceDogs(prev =>
            prev.map(d => d.id === updatedDog.id ? updatedDog : d)
          );
        } else if (updatedDog.status === 'pending') {
          setPendingDogs(prev =>
            prev.map(d => d.id === updatedDog.id ? updatedDog : d)
          );
        }
      }

      return updatedDog;
    } catch (error) {
      console.error('Error updating dog:', error);
      throw error;
    }
  };

  // Delete a dog
  const deleteServiceDog = async (id: string, status: 'active' | 'pending' | 'revoked') => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      const error = new Error('You must be logged in as an admin to delete records');
      console.error('Authentication error:', error);
      throw error;
    }

    try {
      console.log(`Attempting to delete dog with ID: ${id}, Status: ${status}, User: ${user.uid}`);

      // First, get the dog to make sure it exists
      const dogToDelete = await getServiceDogById(id);
      if (!dogToDelete) {
        throw new Error(`Dog with ID ${id} not found`);
      }

      // Delete the dog from Firestore
      await firestoreService.deleteDog(id, status);
      console.log('Dog deleted successfully from Firestore');

      // Update local state
      if (status === 'active' || status === 'revoked') {
        setServiceDogs(prev => prev.filter(dog => dog.id !== id));
      } else if (status === 'pending') {
        setPendingDogs(prev => prev.filter(dog => dog.id !== id));
      }

      return true;
    } catch (error) {
      console.error('Error deleting dog:', error);
      throw error;
    }
  };

  // Approve a pending dog
  const approveDog = async (id: string) => {
    try {
      const approvedDog = await firestoreService.approveDog(id);

      // Update local state
      setPendingDogs(prev => prev.filter(dog => dog.id !== id));
      setServiceDogs(prev => [...prev, approvedDog]);

      return approvedDog;
    } catch (error) {
      console.error('Error approving dog:', error);
      throw error;
    }
  };

  // Deny a pending dog
  const denyDog = async (id: string) => {
    try {
      await firestoreService.denyDog(id);

      // Update local state
      setPendingDogs(prev => prev.filter(dog => dog.id !== id));
    } catch (error) {
      console.error('Error denying dog:', error);
      throw error;
    }
  };

  return (
    <RegistryContext.Provider
      value={{
        serviceDogs,
        pendingDogs,
        loading,
        addServiceDog,
        getServiceDogById,
        getServiceDogByRegistryNumber,
        updateServiceDog,
        deleteServiceDog,
        approveDog,
        denyDog,
        refreshDogs
      }}
    >
      {children}
    </RegistryContext.Provider>
  );
};
